<template>
  <div>
    <v-row>
      <v-col>
        <v-switch
          v-model="drawingType"
          :label="$t('member_registration.waiver_signature.draw_my_signature')"
          true-value="auto"
          false-value="manual"
          :disabled="!autoCanvasReady"
        ></v-switch>
      </v-col>
    </v-row>
    <div v-show="drawingType == drawingTypes.auto">
      <v-row>
        <v-col sm="12" md="6">
          <v-text-field v-model="textSignature" :label="lblWriteYourName"></v-text-field>
        </v-col>
        <v-col sm="12" md="6">
            <v-select
              v-model="font"
              :items="fonts"
              :label="lblChooseFont"
              :no-data-text="$t('common.no_data')"
            ></v-select>
        </v-col>
      </v-row>
    </div>
    <div v-show="drawingType == drawingTypes.auto">
      <v-row cols="12">
        <v-col sm="12">
          <canvas ref="signatureCanvas" style="box-shadow: 0px 0px 3px 0px;"></canvas>
          <v-slider
            v-model="autoSignatureFontSize"
            :min="minFontSize"    
            :max="maxFontSize"  
            step="1"   
            append-icon="mdi-magnify-plus-outline"
            prepend-icon="mdi-magnify-minus-outline"
            @click:append="autoSignatureFontSize++; updateAutoSignature()"
            @click:prepend="autoSignatureFontSize--; updateAutoSignature()"
            color="accent-3"
            @change="updateAutoSignature"
          />
        </v-col>
      </v-row>
    </div>

    <div v-if="drawingType == drawingTypes.manual">
      <VPerfectSignature v-if="loadingCanvas" width="700px" height="200px" :stroke-options="strokeOptions"
        ref="signaturePad" style="border: 1px solid grey;" penColor="#000" @onBegin="bandSignature = true" />
      <v-progress-circular indeterminate class="ma-auto" v-else></v-progress-circular>
      <v-btn class="trashSignature" color="default" small text @click="cleanCanvas">
          <v-icon small>fas fa-trash-alt</v-icon>
      </v-btn>
    </div>    
  </div>
  
</template>

<script>
import VPerfectSignature from 'v-perfect-signature';
import { VProgressCircular, VBtn, VTextField, VSlider, VRow, VCol, VSelect, VIcon, VSwitch } from 'vuetify/lib';

export default {
  components: {
    VPerfectSignature, VProgressCircular, VBtn, VTextField, VSlider, VRow, VCol, VSelect, VIcon, VSwitch
  },
  props: {
    type: {
      type: String,
      default: 'Signature'
    },
    idCanvas: { type: String, default: "0" },
    sizeCanvas: { type: Boolean, default: false }
  },
  data() {
    return {
      x: 0,
      y: 0,
      isDrawing: false,
      canvas: null,
      bandSignature: false,
      widthCanvas: 600,
      windowWidth: 0,
      minFontSize: 10,
      maxFontSize: 100,
      strokeOptions: {
        size: 5,
        thinning: 0.25,
        smoothing: 0.25,
        streamline: 0.25,
        width: "100px"
      },
      autoCanvasReady: false,
      loadingCanvas: false,
      textSignature: '',
      drawingTypes: {
        manual: 'manual',
        auto: 'auto'
      },
      drawingType: 'manual',
      autoSignatureFontSize: 34,
      font: 'Sacramento',
      fonts: [
        'AguafinaScript', 
        'Sacramento'
      ],
      errors: []
    } 
  },
  computed: {
    lblChooseFont() {
      return this.$t('member_registration.waiver_signature.choose_your_signature_font')
    },
    lblWriteYourName() {
      return this.$t('member_registration.waiver_signature.write_your_name')
    },
    isSaveDisabled() {
      const isManualAndEmpty = this.drawingType === this.drawingTypes.manual
        && this.$refs.signaturePad
        && this.$refs.signaturePad.isEmpty();
      const isAutoAndEmpty = this.drawingType === this.drawingTypes.auto && !this.textSignature;
      return isManualAndEmpty || isAutoAndEmpty;
    }
  },
  methods: {
    waitForNextTick() {
      return new Promise((resolve) => this.$nextTick(resolve));
    },
    waitForAnimationFrame() {
      return new Promise((resolve) => requestAnimationFrame(resolve));
    },
    async ensureFontLoaded() {
      const fontFamily = this.font || "Sacramento";
      if (!document.fonts || !document.fonts.load) {
        return;
      }
      try {
        await document.fonts.load(`${this.autoSignatureFontSize || 20}px "${fontFamily}"`);
        await document.fonts.ready;
      } catch (error) {
        // Keep fallback rendering if custom font loading fails.
      }
    },
    getSignatureCanvas() {
      return this.$refs.signatureCanvas || null;
    },
    ensureCanvasSize(canvas) {
      if (!canvas) {
        return;
      }
      if (canvas.width !== 700) {
        canvas.width = 700;
      }
      if (canvas.height !== 200) {
        canvas.height = 200;
      }
    },
    calculateFontLimits() {
      this.$nextTick(() => {
        const canvas = this.getSignatureCanvas();
        if (!canvas) {
          return;
        }
        const signatureText = this.textSignature || '';
        if (!signatureText.trim()) {
          this.minFontSize = 10;
          this.maxFontSize = 100;
          this.autoSignatureFontSize = 34;
          this.updateAutoSignature();
          return;
        }
        const ctx = canvas.getContext("2d");
        this.ensureCanvasSize(canvas);
        let fontSize = 10;
        const fontFamily = this.font || "Sacramento"; 
        ctx.font = `${fontSize}px ${fontFamily}`;
        this.minFontSize = fontSize;
        while (ctx.measureText(signatureText).width < canvas.width && fontSize < 240) {
          fontSize++;
          ctx.font = `${fontSize}px ${fontFamily}`;
        }
        this.maxFontSize = fontSize - 4;
        this.autoSignatureFontSize = Math.max(this.minFontSize, Math.floor(this.maxFontSize / 2));
        this.updateAutoSignature();
      });
    },
    drawLine(x1, y1, x2, y2) {
      let ctx = this.canvas;
      ctx.beginPath();
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 3;
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.closePath();
    },
    beginDrawing(e, type = false) {
      if (!type) {
        this.x = e.offsetX;
        this.y = e.offsetY;
      } else {
        const position = document.getElementById(`myCanvas-${this.idCanvas}`).getBoundingClientRect();
        this.x = e.changedTouches[0].pageX - position.x;
        this.y = e.changedTouches[0].pageY - position.y;
      }

      this.isDrawing = true;
    },
    keepDrawing(e) {
      if (this.isDrawing === true) {
        this.drawLine(this.x, this.y, e.offsetX, e.offsetY);
        this.x = e.offsetX;
        this.y = e.offsetY;
        this.bandSignature = true;
      }
    },
    stopDrawing(e) {
      if (this.isDrawing === true) {
        this.drawLine(this.x, this.y, e.offsetX, e.offsetY);
        this.x = 0;
        this.y = 0;
        this.isDrawing = false;
      }
    },
    cleanCanvas() {
      this.bandSignature = false;
      if (this.drawingType === this.drawingTypes.manual)
      {
        this.$refs.signaturePad.clear();
      }
    },
    async getImage() {
      if (this.drawingType === this.drawingTypes.manual)
      {
        return this.$refs.signaturePad.toDataURL("image/png");
      }
      else
      {
        await this.drawAutoSignatureForExport();
        return this.getBase64FromCanvas();
      }
    },
    fromDataURL(b64) {
      this.$refs.signaturePad.fromDataURL(b64)
    },
    updateAutoSignature(){
      this.renderAutoSignatureWithRetry();
    },
    async drawAutoSignatureForExport() {
      if (this.drawingType !== this.drawingTypes.auto) {
        return;
      }
      await this.waitForNextTick();
      await this.ensureFontLoaded();
      await this.waitForAnimationFrame();
      this.drawText(this.textSignature);
      await this.waitForAnimationFrame();
    },
    renderAutoSignatureWithRetry(tries = 3) {
      this.$nextTick(() => {
        const canvas = this.getSignatureCanvas();
        if (!canvas) {
          if (tries > 0) {
            requestAnimationFrame(() => this.renderAutoSignatureWithRetry(tries - 1));
          }
          return;
        }
        this.drawText(this.textSignature);
      });
    },

    /** canvas */
    drawText(text) {
      this.$nextTick(() => {
      const canvas = this.getSignatureCanvas();
      if (!canvas) {
        return;
      }

      this.ensureCanvasSize(canvas);

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const fontSize = this.autoSignatureFontSize || 20; 
      const fontFamily = this.font || "Arial"; 
      ctx.font = `${fontSize}px '${fontFamily}', cursive`;

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "black"; 

      const x = canvas.width / 2;
      const y = canvas.height / 2;

      const textToDraw = text || "";
      ctx.fillText(textToDraw, x, y);
    });
    },
    exportCanvasImage() {
      const canvas = this.getSignatureCanvas();
      if (!canvas) {
        return;
      }
      const downloadLink = document.createElement("a");

      downloadLink.href = canvas.toDataURL("image/png");
      downloadLink.download = "canvas_image.png";

      downloadLink.click();
    },
    getBase64FromCanvas()
    {
      const canvas = this.getSignatureCanvas();
      if (!canvas) {
        return null;
      }
      return canvas.toDataURL("image/png");
    },
    loadFontsLocal(){
      const fonts = [
        { name: "Sacramento", path: require('@/assets/fonts/sacramento.ttf') },
        { name: "AguafinaScript", path: require('@/assets/fonts/AguafinaScript-Regular.ttf') }
      ];
      fonts.forEach(({ name, path }) => {
        const font = new FontFace(name, `url(${path})`);
        font.load()
          .then((loadedFont) => {
            document.fonts.add(loadedFont);
            if (this.drawingType === this.drawingTypes.auto && this.textSignature) {
              this.renderAutoSignatureWithRetry();
            }
          });
      }); 
    },
    updateAutoCanvasReady() {
      this.$nextTick(() => {
        this.autoCanvasReady = !!this.getSignatureCanvas();
      });
    }
  },
  mounted() {
    const parent = this;
    setTimeout(() => { parent.loadingCanvas = true }, 1000); 
    this.loadFontsLocal();
    this.updateAutoCanvasReady();
  },
  watch: {
    bandSignature(val) {
      this.$emit("changeBandSignature", { value: !val, type: this.type });
    },
    sizeCanvas(val) {
      if (val) {
        this.loadingCanvas = false;
        this.$emit("changeBandSignature", { value: val, type: this.type });
        const parent = this;
        setTimeout(() => {
          parent.loadingCanvas = true;
          this.$emit("changeBandSignature", { value: !val, type: this.type });
        }, 1000);
      }
    },
    /**
     * Sets the signature text based on the drawing type and user session data.
     *
     * If the drawing type is 'manual', it clears the `textSignature`. Otherwise,
     * it sets `textSignature` based on the username or user's first and last name.
     *
     * @param {string} val - The type of drawing (e.g., 'manual' or 'automatic').
     */
    drawingType(val) {
        // Clear textSignature if drawing type is 'manual' and exit
        if (val === 'manual') {
            this.textSignature = null;
            return;
        }

        // Set textSignature based on session data
        const { username, user } = this.$store.state.session;
        this.textSignature = username || `${user.firstName} ${user.lastName}`;

        // Draw the text if available
        this.renderAutoSignatureWithRetry();
        this.calculateFontLimits();
    },

    textSignature(val) {
      if(!val) {
        this.bandSignature = false;
        return;
      }
      this.bandSignature = true;
      this.calculateFontLimits();
    },
    font() {
      if (!this.textSignature) {
        return;
      }
      this.calculateFontLimits();
    }
  }
};
</script>

<style>
.myCanvas {
  border: 1px solid grey;
  min-width: 100%;
  max-width: 100%;
}
</style>